import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CHANTIER_PHASES,
  getChantierMembers,
  getPhaseIndex,
  isChantierOwner,
  updateChantierPhase,
} from '../lib/chantierService';
import ChantierTimelineModal from './ChantierTimelineModal';
import ChantierUpdateModal from './ChantierUpdateModal';
import ChantierUploadModal from './ChantierUploadModal';
import './ChantierCard.css';

const AVATAR_COLORS = ['#F6A500', '#3B82F6', '#10B981', '#EF4444', '#8B5CF6', '#EC4899'];
const MAX_VISIBLE_AVATARS = 4;

// Même photo par défaut que l'app mobile (voir hooks/use-chantier-detail.ts).
const DEFAULT_CHANTIER_PHOTO =
  'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=60&auto=format';

function colorForUser(userId) {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) hash = (hash * 31 + userId.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function initials(name) {
  return (name || '?').trim().charAt(0).toUpperCase();
}

function MemberAvatar({ user }) {
  return (
    <div className="MemberAvatar" title={user.name} style={{ backgroundColor: colorForUser(user.id) }}>
      {user.avatar_url ? (
        <img src={user.avatar_url} alt={user.name} className="MemberAvatar-img" />
      ) : (
        <span>{initials(user.name)}</span>
      )}
    </div>
  );
}

function AvatarStack({ members }) {
  if (members.length === 0) return null;
  const visible = members.slice(0, MAX_VISIBLE_AVATARS);
  const extra = members.length - visible.length;

  return (
    <div className="AvatarStack">
      {visible.map((user) => (
        <MemberAvatar key={user.id} user={user} />
      ))}
      {extra > 0 && <div className="MemberAvatar MemberAvatar-more">+{extra}</div>}
    </div>
  );
}

const PHASES_WITH_DOCUMENT_UPDATE = ['projet', 'cloture'];
const MEMBER_ROLES_WITH_DOCUMENT_UPDATE = ['admin', 'manager'];

// Barre en 4 segments : les phases atteintes prennent la couleur de la phase courante.
// Le propriétaire clique sur un segment pour changer de phase ; les membres la consultent.
function PhaseBar({ phase, editable, saving, onChange }) {
  const currentIndex = getPhaseIndex(phase);
  const current = CHANTIER_PHASES[currentIndex];

  return (
    <div className="PhaseBar">
      <div className="PhaseBar-header">
        <span>Phase</span>
        <span className="PhaseBar-current" style={{ color: current.color }}>
          {current.label}
        </span>
      </div>
      <div className="PhaseBar-segments">
        {CHANTIER_PHASES.map((p, index) => {
          const reached = index <= currentIndex;
          const style = reached ? { backgroundColor: current.color } : undefined;
          return editable ? (
            <button
              key={p.value}
              type="button"
              className="PhaseBar-segment is-editable"
              style={style}
              disabled={saving}
              onClick={() => p.value !== current.value && onChange(p.value)}
              title={`Passer en « ${p.label} »`}
              aria-label={`Passer en « ${p.label} »`}
              aria-pressed={index === currentIndex}
            />
          ) : (
            <span key={p.value} className="PhaseBar-segment" style={style} title={p.label} />
          );
        })}
      </div>
    </div>
  );
}

function ChantierCard({ chantier, userId, onUpdated }) {
  const [isTimelineOpen, setIsTimelineOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const navigate = useNavigate();

  // Le maître d'œuvre (propriétaire) crée et modifie le plan ; les membres ne le voient que s'il existe.
  const hasPlan = (chantier.plans || []).some((plan) => !plan.deleted_at);
  const isOwner = chantier.owner_id === userId;
  const planLabel = hasPlan ? (isOwner ? 'Ouvrir le plan' : 'Voir le plan') : 'Dessiner le plan';

  const [phase, setPhase] = useState(chantier.phase || 'prospect');
  const [savingPhase, setSavingPhase] = useState(false);
  const [phaseError, setPhaseError] = useState(null);
  const currentPhase = CHANTIER_PHASES[getPhaseIndex(phase)];
  const [isOwnerFromDatabase, setIsOwnerFromDatabase] = useState(isOwner);
  const currentMemberRole = members.find((member) => member.id === userId)?.member_role;
  const canUpdateFromDocuments =
    PHASES_WITH_DOCUMENT_UPDATE.includes(phase) &&
    (isOwnerFromDatabase || MEMBER_ROLES_WITH_DOCUMENT_UPDATE.includes(currentMemberRole));

  useEffect(() => {
    setPhase(chantier.phase || 'prospect');
  }, [chantier.phase]);

  async function changePhase(nextPhase) {
    const previous = phase;
    setPhase(nextPhase);
    setPhaseError(null);
    setSavingPhase(true);
    try {
      await updateChantierPhase(chantier.id, nextPhase);
    } catch (err) {
      setPhase(previous);
      setPhaseError(`Changement de phase impossible : ${err.message}`);
    } finally {
      setSavingPhase(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    getChantierMembers(chantier.id)
      .then((data) => {
        if (!cancelled) setMembers(data);
      })
      .catch(() => {});
    isChantierOwner(chantier.id)
      .then((result) => {
        if (!cancelled) setIsOwnerFromDatabase(result);
      })
      .catch((err) => console.error('Erreur vérification propriétaire du chantier:', err));
    return () => {
      cancelled = true;
    };
  }, [chantier.id]);

  return (
    <div className="ChantierCard">
      <div className="ChantierCard-photo">
        <img
          src={chantier.photo_url || DEFAULT_CHANTIER_PHOTO}
          alt={chantier.name}
          className="ChantierCard-photoImg"
        />
        <span
          className="ChantierCard-status"
          style={{ backgroundColor: currentPhase.color, color: currentPhase.textColor }}
        >
          {currentPhase.label}
        </span>
      </div>

      <div className="ChantierCard-body">
        <h3 className="ChantierCard-name">{chantier.name}</h3>
        {(chantier.city || chantier.address) && (
          <p className="ChantierCard-address">
            {[chantier.address, chantier.postal_code, chantier.city].filter(Boolean).join(', ')}
          </p>
        )}

        <PhaseBar phase={phase} editable={isOwner} saving={savingPhase} onChange={changePhase} />
        {phaseError && <p className="ChantierCard-error">{phaseError}</p>}

        <div className="ChantierCard-footer">
          <AvatarStack members={members} />

          <div className="ChantierCard-actions">
            <button
              className="ChantierCard-uploadIconBtn"
              onClick={() => setIsUploadOpen(true)}
              title="Déposer un fichier"
              aria-label="Déposer un fichier"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </button>
            {canUpdateFromDocuments && (
              <button
                className="ChantierCard-uploadIconBtn"
                onClick={() => setIsUpdateOpen(true)}
                title="Mettre à jour le chantier via l'automatisation"
                aria-label="Mettre à jour le chantier via l'automatisation"
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10" />
                  <polyline points="1 20 1 14 7 14" />
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                </svg>
              </button>
            )}
            {(isOwner || hasPlan) && (
              <button
                className="ChantierCard-uploadIconBtn ChantierCard-planBtn"
                onClick={() => navigate(`/chantiers/${chantier.id}/plan`)}
                title={planLabel}
                aria-label={planLabel}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                </svg>
                {hasPlan && <span className="ChantierCard-planDot" />}
              </button>
            )}
            <button className="ChantierCard-timelineBtn" onClick={() => setIsTimelineOpen(true)}>
              Voir la timeline
            </button>
          </div>
        </div>
      </div>

      {isTimelineOpen && (
        <ChantierTimelineModal chantier={chantier} onClose={() => setIsTimelineOpen(false)} />
      )}

      {isUpdateOpen && (
        <ChantierUpdateModal
          chantier={chantier}
          userId={userId}
          onClose={() => setIsUpdateOpen(false)}
          onUpdated={() => {
            setIsUpdateOpen(false);
            onUpdated?.();
          }}
        />
      )}

      {isUploadOpen && (
        <ChantierUploadModal chantier={chantier} userId={userId} onClose={() => setIsUploadOpen(false)} />
      )}
    </div>
  );
}

export default ChantierCard;
